/**
 * Canonical platform tokens used throughout the application catalog UI.
 * Keep this in sync with the `AppPlatform` type in src/types/application.ts.
 */
export type NormalizedPlatform = "ios" | "ipados" | "macos" | "tvos" | "all" | "";

/**
 * Normalizes a platform value (from backend, tab state, or user input) to a
 * canonical lowercase token. Trims whitespace, lowercases, and strips common
 * separators so values like " iOS ", "macOS", "mac_os", and "tv-os" all map
 * to the same canonical form.
 *
 * Returns "" for null/undefined so callers can decide how to treat unknown
 * platforms (typically: don't match any specific platform tab).
 */
export function normalizePlatform(value: string | null | undefined): string {
  if (value == null) return "";
  return value.toString().trim().toLowerCase().replace(/[\s_-]+/g, "");
}

/**
 * Returns true when an application's platform matches the selected platform tab.
 * Both sides go through {@link normalizePlatform} so casing and separators do
 * not affect the result. Null/undefined platforms only match the "all" tab.
 */
export function matchesPlatform(
  appPlatform: string | null | undefined,
  selectedPlatform: string,
): boolean {
  const selected = normalizePlatform(selectedPlatform);
  if (selected === "all") return true;
  const platform = normalizePlatform(appPlatform);
  if (platform === "") return false;
  return platform === selected;
}
